import type { AhaEditor, FullScreenProvider } from '@aha-app/aha-editor-v2';

export interface FullScreenDelegate {
  getContainer: () => HTMLElement | undefined;
  getEnclosingElement?: () => HTMLElement | undefined;
  documentId?: string;
  fullScreenURL?: string;
  nonFullScreenURL?: string;
}

// Editor full screen behaviors.
class AhaFullScreenProvider implements FullScreenProvider {
  type = 'fullScreen' as const;

  urlBeforeFullScreen?: string;

  constructor(
    private readonly ahaEditor: AhaEditor,
    private readonly delegate: FullScreenDelegate
  ) {
    this.onSetFullScreen(this.startInFullscreen, true);
  }

  get startInFullscreen() {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('full_screen') === this.delegate.documentId;
  }

  onGetFullScreen(): boolean {
    return this.isFullScreen;
  }

  get isFullScreen(): boolean {
    return Boolean(this.editorController.state.pluginState.isFullScreen);
  }

  get editorController() {
    return this.ahaEditor.controller;
  }

  onSetFullScreen(fullScreen: boolean, initializing = false): void {
    const wasFullScreen = this.isFullScreen;
    this.editorController.state.pluginState.isFullScreen = fullScreen;

    const container = this.delegate.getContainer();
    const wrapper = container?.closest('.aha-editor-field__wrapper');

    if (this.delegate.fullScreenURL) {
      const fullScreenURL = new URL(
        this.delegate.fullScreenURL,
        window.location.href
      );
      fullScreenURL.search = window.location.search;

      const fullScreenSearchParams = new URLSearchParams(fullScreenURL.search);
      fullScreenSearchParams.set('full_screen', this.delegate.documentId || '');
      fullScreenURL.search = fullScreenSearchParams.toString();
      const fullScreenParam = fullScreenURL.toString();

      if (fullScreen) {
        if (window.location.href.indexOf(fullScreenParam) === -1) {
          this.urlBeforeFullScreen = window.location.href;
          history.replaceState(null, '', fullScreenParam);
        }
      } else if (!initializing) {
        if (this.urlBeforeFullScreen) {
          history.replaceState(null, '', this.urlBeforeFullScreen);
        } else if (this.delegate.nonFullScreenURL === null) {
          // we need to remove the full_screen url param to avoid going into
          // full screen again if we open the same item
          const nonFullScreenURL = new URL(window.location.href);
          const searchParams = new URLSearchParams(nonFullScreenURL.search);
          searchParams.delete('full_screen');
          nonFullScreenURL.search = searchParams.toString();

          history.replaceState(null, '', nonFullScreenURL);
        } else {
          history.replaceState(null, '', this.delegate.nonFullScreenURL);
        }
      }
    }

    if (fullScreen === wasFullScreen) return;

    if (fullScreen) {
      this.selectHiddenTab();
    }

    const sidePanelProvider = this.ahaEditor?.getProvider('sidePanel');
    if (sidePanelProvider) {
      sidePanelProvider.onSetHidden(!fullScreen);
    }

    document.body.classList.toggle('aha-fullscreen', fullScreen);
    wrapper?.classList?.toggle('aha-fullscreen', fullScreen);

    // Wait until the next tick to send events, otherwise some of the elements
    // like the toolbar won't update properly.
    setImmediate(() => {
      this.notifyFullscreenChange();
      this.triggerResizeEvent();
    });
  }

  selectHiddenTab() {
    const hiddenTab = this.delegate
      .getContainer()
      ?.closest('.tab-pane:not(.active)');

    if (hiddenTab && hiddenTab.id) {
      const tabButton = document.querySelector<HTMLLinkElement>(
        `a[href="#${hiddenTab.id}"]`
      );

      if (tabButton) {
        tabButton.click();
      }
    }
  }

  private notifyFullscreenChange() {
    // It is not possible to specify an origin for the post message here as the parent is
    // the customers site which is hosting the widget. There is no sensitive data being
    // transmitted here only a generic eventName.
    if (window.parent) {
      window.parent.postMessage({ eventName: 'ahaFeedback:fullscreen' }, '*');
    }
  }

  private triggerResizeEvent() {
    window.dispatchEvent(new Event('resize'));
  }
}

export default AhaFullScreenProvider;
